import type {
  AuthCtx,
  Citation,
  GroundedAnswerInput,
  GroundedClaimInput,
  GroundingBudget,
  GroundingRuntime,
  SearchOptions,
  SearchResponse,
} from "@gencow/core";
import type { SQL } from "drizzle-orm";
import type { RagMetricInput } from "./rag-operations-metrics.js";
import { buildTargetedResearchQueries } from "./server-claim-gap-analysis.js";
import { verifyClaims } from "./server-claim-verifier.js";
import { finalizeGroundedAnswer } from "./server-grounded-answer-finalizer.js";
import { buildCanonicalCitations } from "./server-grounding-citations.js";
import { dedupeCitations, resolveGroundingBudget } from "./server-grounding-common.js";

type GroundingDbLike = {
  execute(query: SQL): Promise<unknown>;
};

type GroundingSearchFn = (table: string, query: string, options: SearchOptions) => Promise<SearchResponse>;

export function createGroundingRuntime(params: {
  db: GroundingDbLike;
  auth: AuthCtx;
  search: GroundingSearchFn;
  observeRagAnswer?: (event: Omit<RagMetricInput, "appId">) => Promise<void> | void;
}): GroundingRuntime {
  return {
    async answer(input: GroundedAnswerInput) {
      const startedAt = Date.now();
      const budget = resolveGroundingBudget(input.budget);
      const claims = normalizeClaims(input.claims, input.question, budget.maxClaimsPerAnswer);
      const mode = input.mode ?? "qa";
      let citations = await retrieveCitations(params, input, input.question, budget);
      let verified = verifyClaims({
        claims,
        citations,
        mode,
        compare: input.compare,
        topic: input.topic,
        budget,
      });

      for (let loop = 0; loop < budget.maxVerifyLoops && hasGaps(verified); loop += 1) {
        const queries = buildTargetedResearchQueries({
          question: input.question,
          claims: verified,
          mode,
          compare: input.compare,
          budget,
        });
        if (queries.length === 0) break;

        const extra = await retrieveQueries(params, input, queries, budget);
        citations = dedupeCitations([...citations, ...extra]);
        verified = verifyClaims({
          claims,
          citations,
          mode,
          compare: input.compare,
          topic: input.topic,
          budget,
        });
      }

      const answer = finalizeGroundedAnswer(verified);
      await observeAnswerMetrics(params, {
        input,
        answer,
        mode,
        startedAt,
      });
      return answer;
    },
  };
}

function normalizeClaims(
  rawClaims: GroundedClaimInput[] | undefined,
  question: string,
  maxClaims: number,
): GroundedClaimInput[] {
  const claims = rawClaims?.length ? rawClaims : [{ claimText: question }];
  const normalized = claims
    .map((claim, index) => ({
      claimId: claim.claimId ?? `claim-${index + 1}`,
      claimText: claim.claimText.trim(),
      ...(claim.requiredTerms ? { requiredTerms: claim.requiredTerms } : {}),
    }))
    .filter((claim) => claim.claimText.length > 0)
    .slice(0, maxClaims);

  if (normalized.length === 0) {
    throw new Error("Grounded answer requires at least one non-empty claim");
  }
  return normalized;
}

async function retrieveQueries(
  deps: { db: GroundingDbLike; auth: AuthCtx; search: GroundingSearchFn },
  input: GroundedAnswerInput,
  queries: string[],
  budget: GroundingBudget,
): Promise<Citation[]> {
  const batches = await Promise.all(queries.map((query) => retrieveCitations(deps, input, query, budget)));
  return dedupeCitations(batches.flat());
}

async function retrieveCitations(
  deps: { db: GroundingDbLike; auth: AuthCtx; search: GroundingSearchFn },
  input: GroundedAnswerInput,
  query: string,
  budget: GroundingBudget,
): Promise<Citation[]> {
  const response = await deps.search("rag_chunks", query, {
    fields: ["chunk_text", "lexical_text"],
    limit: Math.max(8, budget.maxCitationsPerClaim * 4),
    scope: input.scope,
    filters: input.filters,
  });

  return buildCanonicalCitations({
    db: deps.db,
    auth: deps.auth,
    scope: input.scope,
    hits: response.items,
  });
}

function hasGaps(claims: Array<{ status: string }>): boolean {
  return claims.some((claim) => claim.status !== "supported");
}

function resolveMetricScope(auth: AuthCtx, scope: SearchOptions["scope"]) {
  if (scope.visibility === "private") {
    return {
      corpus: scope.corpus.trim(),
      visibility: scope.visibility,
      ownerUserId: scope.ownerUserId ?? auth.requireAuth().id,
    };
  }

  return {
    corpus: scope.corpus.trim(),
    visibility: scope.visibility,
    ownerUserId: scope.ownerUserId ?? null,
  };
}

async function observeAnswerMetrics(
  params: {
    auth: AuthCtx;
    observeRagAnswer?: (event: Omit<RagMetricInput, "appId">) => Promise<void> | void;
  },
  args: {
    input: GroundedAnswerInput;
    answer: Awaited<ReturnType<typeof finalizeGroundedAnswer>>;
    mode: NonNullable<GroundedAnswerInput["mode"]>;
    startedAt: number;
  },
): Promise<void> {
  if (!params.observeRagAnswer) return;

  const claimCount = args.answer.claims.length;
  const groundedClaimCount = args.answer.claims.filter((claim) => claim.status === "supported").length;
  const base = {
    ...resolveMetricScope(params.auth, args.input.scope),
    operation: "answer" as const,
    metadata: {
      mode: args.mode,
      grounded: args.answer.grounded,
      warningCount: args.answer.warnings.length,
    },
  };

  try {
    await Promise.all([
      params.observeRagAnswer({
        ...base,
        metricName: "latency_ms",
        metricValue: Math.max(0, Date.now() - args.startedAt),
        unit: "ms",
      }),
      params.observeRagAnswer({
        ...base,
        metricName: "claim_count",
        metricValue: claimCount,
        unit: "count",
      }),
      params.observeRagAnswer({
        ...base,
        metricName: "grounded_claim_count",
        metricValue: groundedClaimCount,
        unit: "count",
      }),
      params.observeRagAnswer({
        ...base,
        metricName: "citation_count",
        metricValue: args.answer.citations.length,
        unit: "count",
      }),
      params.observeRagAnswer({
        ...base,
        metricName: "citation_attached_ratio",
        metricValue: claimCount === 0 ? 0 : groundedClaimCount / claimCount,
      }),
    ]);
  } catch {
    // Observability must not change grounding behavior.
  }
}
